import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, authHeader, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);          // null = checking, false = anon, object = logged in
  const [token, setToken] = useState(() => localStorage.getItem("cec_token") || null);

  const refresh = useCallback(async () => {
    // Avoid noisy 401s on public pages — only check session when a token is present
    // or when the user explicitly visits an admin route.
    const onAdmin = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    if (!token && !onAdmin) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me", { headers: authHeader(token) });
      setUser(data);
    } catch (_) {
      setUser(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.token) {
        localStorage.setItem("cec_token", data.token);
        setToken(data.token);
      }
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout", null, { headers: authHeader(token) }); } catch {}
    localStorage.removeItem("cec_token");
    setToken(null);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, token, login, logout, refresh, headers: authHeader(token) }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
